import { Input, Select } from "antd";
import { Plus, X } from "lucide-react";
import { nanoid } from "nanoid";
import { RESUME_SECTION_TYPES } from "../../../optimizeResume/editor/config";
import SectionCardShell from "../../../optimizeResume/editor/SectionCardShell";

const blankItem = () => ({
  id: `edu-${nanoid(6)}`,
  institution: "",
  degree: "",
  specialization: "",
  startDate: "",
  endDate: "",
  gpa: "",
  location: "",
  normalizedLevelOfDegree: "",
  honors: [],
  coursework: [],
});

export default function Education({
  id,
  data,
  onChange,
  onDelete,
  allCollapsed,
}) {
  const items = data ?? [];

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };

  const removeItem = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const addItem = () => {
    onChange([...items, blankItem()]);
  };

  return (
    <SectionCardShell
      id={id}
      type={RESUME_SECTION_TYPES.EDUCATION}
      data={data}
      onChange={onChange}
      onDelete={onDelete}
      allCollapsed={allCollapsed}
    >
      <div className="flex flex-col gap-4">
        {items.map((it, idx) => (
          <div
            key={it.id}
            className="border border-gray-200 rounded-lg p-3 bg-gray-50/40"
          >
            <div className="flex justify-between items-start mb-2">
              <p className="font-medium text-sm text-[#344054]">
                {it.institution || `Education #${idx + 1}`}
              </p>
              <button
                onClick={() => removeItem(idx)}
                className="text-gray-400 hover:text-red-600"
              >
                <X size={16} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="School / University"
                value={it.institution}
                onChange={(e) => updateItem(idx, { institution: e.target.value })}
              />
              <Input
                placeholder="Location"
                value={it.location}
                onChange={(e) => updateItem(idx, { location: e.target.value })}
              />
              <Input
                placeholder="Degree (B.S. / M.B.A. / CA / B.A. / etc.)"
                value={it.degree}
                onChange={(e) => updateItem(idx, { degree: e.target.value })}
              />
              <Input
                placeholder="Field / Concentration"
                value={it.specialization}
                onChange={(e) => updateItem(idx, { specialization: e.target.value })}
              />
              <Input
                placeholder="Start (YYYY-MM)"
                value={it.startDate}
                onChange={(e) => updateItem(idx, { startDate: e.target.value })}
              />
              <Input
                placeholder="End (YYYY-MM)"
                value={it.endDate}
                onChange={(e) => updateItem(idx, { endDate: e.target.value })}
              />
              <Input
                placeholder="GPA / Grade (e.g. 3.8/4.0, First Class, 8.5 CGPA)"
                value={it.gpa}
                onChange={(e) => updateItem(idx, { gpa: e.target.value })}
              />
              <Input
                placeholder="Normalized degree (optional)"
                value={it.normalizedLevelOfDegree}
                onChange={(e) => updateItem(idx, { normalizedLevelOfDegree: e.target.value })}
              />
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3">
              <label className="flex flex-col gap-1 text-xs text-[#667085]">
                <span>Honors / Awards</span>
                <Select
                  mode="tags"
                  value={it.honors ?? []}
                  onChange={(vals) => updateItem(idx, { honors: vals })}
                  placeholder="e.g. Magna Cum Laude, Dean's List, Gold Medalist"
                  tokenSeparators={[","]}
                />
              </label>
              {/* <label className="flex flex-col gap-1 text-xs text-[#667085]">
                <span>Activities / Clubs / Leadership</span>
                <Select
                  mode="tags"
                  value={it.activities ?? []}
                  onChange={(vals) => updateItem(idx, { activities: vals })}
                  placeholder="e.g. ACM Captain, Drama Club Secretary"
                  tokenSeparators={[","]}
                />
              </label> */}
              <label className="flex flex-col gap-1 text-xs text-[#667085]">
                <span>Relevant Coursework</span>
                <Select
                  mode="tags"
                  value={it.coursework ?? []}
                  onChange={(vals) => updateItem(idx, { coursework: vals })}
                  placeholder="e.g. Financial Accounting, Auditing, Distributed Systems"
                  tokenSeparators={[","]}
                />
              </label>
            </div>
          </div>
        ))}
        <button
          onClick={addItem}
          className="self-start flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus size={14} /> Add education
        </button>
      </div>
    </SectionCardShell>
  );
}
